import { Stack } from "@altimateai/lego";
import { IconAlertTriangle } from "@tabler/icons-react";
import type { TokenUsageResponse } from "../types";
import type { WalletResponse } from "../types";
import { cn } from "@/lib/utils";

const formatTokens = (tokens: number): string => {
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(2)}M`;
  if (tokens >= 1_000) return `${(tokens / 1_000).toFixed(1)}K`;
  return tokens.toLocaleString();
};

const formatDollars = (cents: number): string => `$${(cents / 100).toFixed(2)}`;

interface OverageStatusCardProps {
  tokenUsage: TokenUsageResponse;
  walletData: WalletResponse | undefined;
  isCommunity: boolean;
}

export const OverageStatusCard = ({
  tokenUsage,
  walletData,
  isCommunity,
}: OverageStatusCardProps) => {
  const overageTokens = tokenUsage.overage_tokens;
  const overageRate = tokenUsage.overage_rate_cents_per_million;
  const isInOverage = overageTokens > 0;
  const overageCostCents = Math.ceil((overageTokens / 1_000_000) * overageRate);

  const walletBalance = walletData?.balance_cents ?? 0;
  const spendCap = walletData?.spend_cap_cents;
  const spendThisPeriod = walletData?.spend_this_period_cents ?? 0;

  // Calculate how close to limit
  const totalTokenPool = (tokenUsage.total_tokens ?? 0) + (walletData?.balance_tokens ?? 0);
  const tokensConsumed = (tokenUsage.total_consumed ?? 0) + overageTokens;
  const usagePercent = totalTokenPool > 0 ? (tokensConsumed / totalTokenPool) * 100 : 0;
  const isApproachingLimit = usagePercent >= 80 && usagePercent < 100;
  const isAtLimit = usagePercent >= 100 && !isInOverage;

  // No warnings needed if usage is below 80%
  if (!isApproachingLimit && !isAtLimit && !isInOverage) {
    return null;
  }

  return (
    <div
      className={cn(
        "al-rounded-lg al-border al-p-4",
        isInOverage
          ? "al-bg-red-50 al-border-red-200"
          : isAtLimit
            ? "al-bg-orange-50 al-border-orange-200"
            : "al-bg-yellow-50 al-border-yellow-200"
      )}
    >
      <Stack direction="row" gap="sm" align="start">
        <IconAlertTriangle
          size={16}
          className={cn(
            "al-mt-0.5 al-flex-shrink-0",
            isInOverage
              ? "al-text-red-600"
              : isAtLimit
                ? "al-text-orange-600"
                : "al-text-yellow-600"
          )}
        />
        <div className="al-space-y-1">
          {isInOverage && (
            <>
              <span className="al-text-sm al-font-medium al-text-red-800">
                You are using overage tokens
              </span>
              <div className="al-text-xs al-text-red-700">
                <span>
                  {formatTokens(overageTokens)} overage tokens consumed at{" "}
                  {formatDollars(overageRate)}/M tokens = {formatDollars(overageCostCents)} charged
                  to your wallet.
                </span>
                {walletBalance > 0 && (
                  <span className="al-ml-1">Wallet balance: {formatDollars(walletBalance)}.</span>
                )}
              </div>
              {spendCap != null && (
                <div className="al-text-xs al-text-red-600">
                  Spending limit: {formatDollars(spendThisPeriod)} / {formatDollars(spendCap)} used.
                  {spendThisPeriod >= spendCap && (
                    <span className="al-font-medium">
                      {" "}
                      Limit reached — additional usage will require BYOK.
                    </span>
                  )}
                </div>
              )}
            </>
          )}

          {isAtLimit && !isInOverage && (
            <>
              <span className="al-text-sm al-font-medium al-text-orange-800">
                Token allowance fully used
              </span>
              <div className="al-text-xs al-text-orange-700">
                {isCommunity ? (
                  <span>
                    Your one-time token grant is fully used. Connect your own API key (BYOK) to
                    continue using AI features.
                  </span>
                ) : walletBalance > 0 ? (
                  <span>
                    Additional usage will draw from your wallet ({formatDollars(walletBalance)}{" "}
                    remaining) at {formatDollars(overageRate)}/M tokens.
                  </span>
                ) : (
                  <span>
                    Add funds to your wallet or upgrade your plan to continue. Overage rate:{" "}
                    {formatDollars(overageRate)}/M tokens.
                  </span>
                )}
              </div>
            </>
          )}

          {isApproachingLimit && !isAtLimit && !isInOverage && (
            <>
              <span className="al-text-sm al-font-medium al-text-yellow-800">
                Approaching token limit ({Math.round(usagePercent)}% used)
              </span>
              <div className="al-text-xs al-text-yellow-700">
                {isCommunity ? (
                  <span>
                    Your one-time token grant is running low. Consider connecting your own API key
                    (BYOK) for unlimited usage at no extra cost.
                  </span>
                ) : (
                  <span>
                    When your allowance is used up, additional tokens will be charged at{" "}
                    {formatDollars(overageRate)}/M from your wallet.
                    {walletBalance > 0
                      ? ` Wallet balance: ${formatDollars(walletBalance)}.`
                      : " Add funds to your wallet to avoid interruption."}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </Stack>
    </div>
  );
};
